import React, { useState, useEffect } from 'react';
import './SupportModal.scss';
import axios from '@/Data/Axios';
import { createI18n } from '@/Utils/i18n';
import { useSession } from '@/Recoil/Session/Session';
import { useUsers } from '@/Recoil/User';

const translations = {
    'Support Request': { id: 'Permintaan Dukungan', ko: '지원 요청' },
    'How can we help you?': { id: 'Bagaimana kami bisa membantu Anda?', ko: '어떻게 도와드릴까요?' },
    'Full Name': { id: 'Nama Lengkap', ko: '성명' },
    'Email Address': { id: 'Alamat Email', ko: '이메일 주소' },
    'Phone Number': { id: 'Nomor Telepon', ko: '전화번호' },
    'Message': { id: 'Pesan', ko: '메시지' },
    'Contact Support': { id: 'Hubungi Dukungan', ko: '지원팀 연락' },
    'Processing...': { id: 'Memproses...', ko: '처리 중...' },
    'Required field': { id: 'Bidang wajib', ko: '필수 입력' },
    'Registration successful': { id: 'Pendaftaran berhasil', ko: '등록 완료' },
    'Registration failed': { id: 'Pendaftaran gagal', ko: '등록 실패' }
};

const t = createI18n(translations);

interface FormData {
    name: string;
    email: string;
    phone: string;
    message: string;
}

interface SupportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
    const { session } = useSession();
    const { syncProfiles, updateProfile } = useUsers();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        name: session.name ?? '',
        email: session.email ?? '',
        phone: '',
        message: ''
    });
    useEffect(() => {
        if (session.addressid) {
            syncProfiles([session.addressid]);
        }
        setFormData(prev => ({
            ...prev,
            name: session.name ?? '',
            email: session.email ?? ''
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.phone) {
            alert(t('Required field'));
            return;
        }

        updateProfile({
            name: formData.name,
            email: formData.email,
            phonenumber: formData.phone
        });

        setIsSubmitting(true);

        try {
            const currentDate = new Date().toLocaleString();
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Support Request</title>
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f4f4f4; }
                        .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
                        .header { background: linear-gradient(135deg, #7b4397 0%, #dc2430 100%); color: white; padding: 30px; text-align: center; }
                        .content { padding: 30px; }
                        .info-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                        .info-table th, .info-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                        .info-table th { background-color: #f8f9fa; font-weight: bold; }
                        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; color: #666; font-size: 14px; }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="header">
                            <h1>🎓 Support Request</h1>
                            <p>New support request from admission page</p>
                        </div>
                        <div class="content">
                            <h2>Support Details</h2>
                            <table class="info-table">
                                <tr>
                                    <th>Field</th>
                                    <th>Response</th>
                                </tr>
                                <tr>
                                    <td><strong>Name</strong></td>
                                    <td>${formData.name}</td>
                                </tr>
                                <tr>
                                    <td><strong>Email</strong></td>
                                    <td>${formData.email}</td>
                                </tr>
                                <tr>
                                    <td><strong>Phone</strong></td>
                                    <td>${formData.phone}</td>
                                </tr>
                                <tr>
                                    <td><strong>Message</strong></td>
                                    <td>${formData.message || 'None'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Request Time</strong></td>
                                    <td>${currentDate}</td>
                                </tr>
                            </table>
                        </div>
                        <div class="footer">
                            <p>This email was generated from the Admission support request.</p>
                            <p>Korean Mate • <a href="https://koreanmate.app">koreanmate.app</a></p>
                        </div>
                    </div>
                </body>
                </html>
            `;

            const response = await axios.post('/admin/trigger-email', {
                email: 'dl_mate@webtoon.today',
                subject: `[🎓Support Request] ${formData.name}`,
                body: htmlContent,
                groups: 'dl_mate@webtoon.today'
            });

            if (response.data && response.data.code === 200) {
                alert(t('Registration successful'));
                setFormData({ name: '', email: '', phone: '', message: '' });
                onClose();
            } else {
                throw new Error('Email sending failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert(t('Registration failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormData({ name: '', email: '', phone: '', message: '' });
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-overlay" onClick={onClose} />
            <div className="waiting-list-modal" style={{ zIndex: 1002 }}>
                <button className="close-button" onClick={onClose}>×</button>
                <h2>{t('Support Request')}</h2>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <p>{t('How can we help you?')}</p>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                        <div className="form-group">
                            <label htmlFor="name">{t('Full Name')} *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                placeholder={t('Full Name')}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">{t('Phone Number')} *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                placeholder="+62 812345678"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{t('Email Address')} *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">{t('Message')}</label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder="How can we help you?"
                        />
                    </div>

                    <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('Processing...') : t('Contact Support')}
                    </button>
                </form>
            </div>
        </>
    );
};

export default SupportModal;